import React, { useState } from 'react';
import { MapPin, Locate, AlertCircle } from 'lucide-react';
import { useFormContext, Controller } from 'react-hook-form';
import { usePropertyData } from '../../../../hooks/usePropertyData';
import { toast } from 'react-toastify';
import MapCoordinatesEditor from '../../../../components/admin/property/components/MapCoordinatesEditor';
import CopyableInput from '../../../../components/admin/property/components/CopyableInput';
import LocationGuide from '../../../../components/admin/property/components/LocationGuide';

interface PropertyLocationSectionProps {
  className?: string;
}

const PropertyLocationSection: React.FC<PropertyLocationSectionProps> = ({ className = '' }) => {
  const { watch, setValue, register, control } = useFormContext();
  const { provinces, zones } = usePropertyData();
  const provinceId = watch('province_id');
  const location = watch('location');
  const zoneId = watch('zone_id');
  const latitude = watch('latitude');
  const longitude = watch('longitude');
  
  const handleCoordinatesChange = (lat: number, lng: number) => {
    setValue('latitude', lat);
    setValue('longitude', lng);
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm p-4 md:p-6 mb-6 ${className}`}>
      <div className="flex items-center justify-between mb-4 pb-2 border-b">
        <div className="flex items-center">
          <MapPin className="h-5 w-5 text-primary mr-2" />
          <h4 className="font-medium text-gray-700">Ubicazione</h4>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CopyableInput
          name="province_id"
          label="Città/Comune"
          icon={MapPin}
          required
          value={provinceId || ''}
          onChange={(e) => setValue('province_id', e.target.value)}
          type="select"
          options={provinces.map(province => ({ value: province.id, label: province.name }))}
        />

        <CopyableInput
          name="zone_id"
          label="Zona"
          icon={MapPin}
          required
          disabled={!provinceId}
          value={zoneId || ''}
          onChange={(e) => setValue('zone_id', e.target.value)}
          type="select"
          options={zones
            .filter(zone => zone.province_id === provinceId)
            .map(zone => ({ value: zone.id, label: zone.name }))}
        />

        {/* Indirizzo a tutta larghezza: la nota sul formato ha bisogno di spazio */}
        <div className="md:col-span-2">
          <CopyableInput
            name="location"
            label="Indirizzo"
            icon={MapPin}
            required
            value={location || ''}
            onChange={(e) => setValue('location', e.target.value)}
            placeholder="Es. Via Roma 450, 50018 Scandicci FI"
          />

          <div className="mt-2 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-3">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-amber-900">
                  Importante: formato dell'indirizzo
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  Inserisci via e numero civico, CAP, comune e sigla della provincia. Esempio:
                </p>
                <p className="text-sm font-mono font-medium text-amber-900 bg-white rounded px-2 py-1.5 mt-1.5 break-words">
                  Via Roma 450, 50018 Scandicci FI
                </p>
                <p className="text-sm text-amber-800 mt-2">
                  Per l'indirizzo esatto puoi cercarlo nella mappa qui sotto: ti permette di
                  individuare il CAP corretto e la zona, oltre a inserire le coordinate precise.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:col-span-2">
          <CopyableInput
            name="latitude"
            label="Latitudine"
            icon={MapPin}
            value={latitude?.toString() || ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseFloat(e.target.value);
              setValue('latitude', value);
            }}
            type="number"
            placeholder="Es. 43.756111"
          />
          
          <CopyableInput
            name="longitude"
            label="Longitudine"
            icon={MapPin}
            value={longitude?.toString() || ''}
            onChange={(e) => {
              const value = e.target.value === '' ? null : parseFloat(e.target.value);
              setValue('longitude', value);
            }}
            type="number"
            placeholder="Es. 11.178889"
          />
        </div>
        
        {/* Map for visual coordinate selection */}
        <div className="md:col-span-2">
          <LocationGuide />
          
          <MapCoordinatesEditor
            latitude={latitude}
            longitude={longitude}
            onCoordinatesChange={handleCoordinatesChange}
          />
        </div>
      </div>
    </div>
  );
};

export default PropertyLocationSection;